import { useEffect, useRef } from 'react'
import { api } from '@/api/client'
import type { SystemInfo } from './types'

export type SystemInfoLiveEvent =
  | { type: 'ping' }
  | { type: 'system'; info: SystemInfo }
  | { type: 'error'; message?: string }

type Handlers = {
  onInfo?: (info: SystemInfo) => void
  onError?: () => void
  onOpen?: () => void
}

/**
 * SSE подписка на системные метрики терминала (CPU/RAM/Disk).
 * Same-origin cookies (session) — как у REST api().
 */
export function useSystemInfoLive(
  terminalId: number | string | null | undefined,
  enabled: boolean,
  handlers: Handlers,
) {
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  useEffect(() => {
    if (!enabled || terminalId == null || terminalId === '') return

    const id = String(terminalId)
    let es: EventSource | null = null
    let closed = false
    let fallbackTimer: ReturnType<typeof setInterval> | null = null

    const clearFallback = () => {
      if (fallbackTimer) {
        clearInterval(fallbackTimer)
        fallbackTimer = null
      }
    }

    const fetchFallback = () => {
      void api<SystemInfo>(`/api/terminals/${id}/statuscpu`)
        .then((info) => {
          handlersRef.current.onInfo?.(info)
        })
        .catch(() => {
          handlersRef.current.onError?.()
        })
    }

    const startFallback = () => {
      if (fallbackTimer) return
      fetchFallback()
      fallbackTimer = setInterval(fetchFallback, 15000)
    }

    const connect = () => {
      if (closed) return
      es = new EventSource(`/api/terminals/${id}/statuscpu/live`, {
        withCredentials: true,
      })

      es.addEventListener('open', () => {
        clearFallback()
        handlersRef.current.onOpen?.()
      })

      es.addEventListener('system', (msg) => {
        try {
          const data = JSON.parse((msg as MessageEvent).data) as SystemInfoLiveEvent
          if (data?.type === 'ping') return
          if (data?.type === 'system' && data.info) {
            handlersRef.current.onInfo?.(data.info)
            return
          }
          if (data?.type === 'error') {
            handlersRef.current.onError?.()
          }
        } catch {
          /* ignore bad payload */
        }
      })

      es.onerror = () => {
        // Браузер сам переподключает EventSource; REST fallback — пока канал мёртв
        startFallback()
      }
    }

    connect()

    return () => {
      closed = true
      clearFallback()
      try {
        es?.close()
      } catch {
        /* ignore */
      }
    }
  }, [terminalId, enabled])
}
